import type { ConceptSeverity, ValidationFinding } from '@/services/api/schemas'

/** Severity ordering, worst-first, for picking the badge a row should show when
 *  more than one finding lands on it. */
const SEVERITY_RANK: Record<ConceptSeverity, number> = { error: 3, warning: 2, hint: 1, meta: 0 }

export interface LocationSeverities {
  entities: Record<string, ConceptSeverity>
  relations: Record<string, ConceptSeverity>
  patterns: Record<string, ConceptSeverity>
}

/** The location string a pattern finding carries — mirrors the validator's
 *  `pattern:(Src)-[Rel]->(Tgt)` format so a row can look itself up. */
export function patternKey(pattern: {
  source_type: string
  relationship_type: string
  target_type: string
}): string {
  return `(${pattern.source_type})-[${pattern.relationship_type}]->(${pattern.target_type})`
}

type ParsedLocation = { kind: keyof LocationSeverities; key: string } | null

/** Parse a finding `location` back to the entity/relation/pattern it belongs to.
 *  Returns `null` for schema-level (`schema`) or unrecognized locations, which
 *  have no list row to badge. Locations are the concept-validator format:
 *  `entity:Label`, `entity:Label.prop`, `relationship:Label[.prop]`,
 *  `pattern:(Src)-[Rel]->(Tgt)`. */
function parseLocation(location: string): ParsedLocation {
  if (location.startsWith('entity:')) {
    return { kind: 'entities', key: location.slice('entity:'.length).split('.')[0] }
  }
  if (location.startsWith('relationship:')) {
    return { kind: 'relations', key: location.slice('relationship:'.length).split('.')[0] }
  }
  if (location.startsWith('pattern:')) {
    return { kind: 'patterns', key: location.slice('pattern:'.length) }
  }
  return null
}

/** Reduce findings to the single worst severity per offending entity/relation/
 *  pattern, so list rows can show one inline severity badge. Property-level
 *  findings (`entity:Label.prop`) fold up onto their owning row; schema-level
 *  findings are dropped (no row to badge). */
export function locationSeverities(findings: ValidationFinding[] | null): LocationSeverities {
  const out: LocationSeverities = { entities: {}, relations: {}, patterns: {} }
  for (const finding of findings ?? []) {
    const parsed = parseLocation(finding.location)
    if (!parsed) continue
    const bucket = out[parsed.kind]
    const prev = bucket[parsed.key]
    if (prev === undefined || SEVERITY_RANK[finding.severity] > SEVERITY_RANK[prev]) {
      bucket[parsed.key] = finding.severity
    }
  }
  return out
}
